<template>
  <main class="choose-date-time" v-show="showChooseDateTime">
    <!-- Works for both normal flow + registration -->
    <h1 class="title title--desktop" v-t="'message.choose_date_time'"></h1>
    <div class="pp-layout-container">
      <div class="pp-preamble">
        <h1 v-t="'message.choose_date_time'" class="title title--mobile"></h1>
        <template v-if="!shouldDisplayShowTitle">
          <p class="taxonomy-link" v-show="event.venue">{{ event.venue }}</p>
        </template>
        <p v-if="getSponsor" class="pp-event__sponsor">{{ getSponsor }}</p>
        <template v-if="!shouldDisplayShowTitle">
          <h2 class="pp-event__title" v-show="event.title">{{ event.title }}</h2>
          <h2 class="pp-event__title" v-show="getTitle">{{ getTitle }}</h2>
        </template>
        <!-- {{ event.advisory }} -->

        <div v-if="event.advisory"
          style="background-color: rgb(245, 245, 245);padding: 5px 15px;margin-bottom: 10px;border-radius: 5px; font-size: 0.875rem;"
          v-html="event.advisory" />
      </div>

      <section class="pp-main-content">
        <div class="content">
          <!-- Purchase path -->
          <ol class="pp-event-item-list" v-if="!onRegistrationPath">
            <!-- Loop through performances -->
            <event-item v-for="event in itemsToDisplay" :key="event.productId" :itemInfo="event"
              :itemSelected="itemSelected == event.productId" @selectItem="selectItem($event)"
              :showTitle="event.showTitle" :shouldDisplayShowTitle="shouldDisplayShowTitle"
              :shouldDisplayLocalisedDate="shouldDisplayLocalisedDate" />
          </ol>

          <!-- Registration path -->
          <ol class="pp-event-item-list" v-else>
            <!-- Loop through sessions -->
            <session-item v-for="(session, index) in itemsToDisplay" :key="`${session.CRMEventID}.${index}`"
              :itemInfo="session" :itemSelected="itemSelected == session.CRMEventID"
              :itemStatus="event.sessionsStatus[session.CRMEventID]" @selectItem="selectItem($event)"
              :showTitle="event.showTitle" :shouldDisplayShowTitle="shouldDisplayShowTitle"
              :shouldDisplayLocalisedDate="shouldDisplayLocalisedDate" />
          </ol>
        </div>

        <div class="choose-date-time__desktop-actions">
          <!-- Purchase Path submit (desktop) -->
          <fancy-button :icon="['far', 'ticket-alt']" :text="$t('buttons.seat_area_and_tickets')"
            :disabled="itemSelected == null" @click.native="ppSubmitCheck" v-if="!onRegistrationPath" />

          <!-- Registration Path submit (desktop) -->
          <fancy-button :icon="['far', 'ticket-alt']" :text="$t('register.select_session_title')"
            :disabled="itemSelected == null" @click.native="registrationSubmit" v-else />
        </div>

      </section>

      <!-- Desktop actions -->
      <aside class="pp-sidebar">
        <p v-t="'message.calendar_prompt'"></p>
        <p v-t="'message.calendar_instruction'"></p>

        <datepicker :inline="true" :monday-first="true" :language="calendarLanguage"
          :class="!calendarVisible ? 'calendar--hidden' : ''" :highlighted="highlightedDates" :open-date="openDate"
          :value="calendarDateSelected" :full-month-name="true" :use-utc="true" @selected="selectDate"
          :disabledDates="{ to: new Date(Date.now() - 8640000) }" />
      </aside>

      <!-- Mobile actions -->
      <div class="pp-actions">
        <div class="pp-actions__container">
          <datepicker :inline="true" :monday-first="true" :language="calendarLanguage"
            :class="!calendarVisible ? 'calendar--hidden' : ''" :highlighted="highlightedDates" :open-date="openDate"
            :value="calendarDateSelected" :full-month-name="true" @selected="selectDate"
            :disabledDates="{ to: new Date(Date.now() - 8640000) }" />

          <!-- Purchase Path submit -->
          <fancy-button :icon="['far', 'ticket-alt']" :text="$t('buttons.seat_area_and_tickets')"
            :disabled="itemSelected == null" @click.native="ppSubmitCheck" v-if="!onRegistrationPath" />

          <!-- Registration Path submit -->
          <fancy-button :icon="['far', 'ticket-alt']" :text="$t('register.select_session_title')"
            :disabled="itemSelected == null" @click.native="registrationSubmit" v-else />

          <!-- Moible show calendar -->
          <button class="btn--secondary" v-show="!calendarVisible" @click="toggleCalendar"
            v-t="'buttons.view_calendar'"></button>

          <!-- Mobile hide calendar -->
          <button class="btn--secondary" v-show="calendarVisible" @click="toggleCalendar"
            v-t="'buttons.hide_calendar'"></button>
        </div>
      </div>
    </div>


    <!-- This is only relevant to PP, not registration -->
    <modal-advisory :advisory="event.advisory" class="advisory" @advisoryConfirmed="submitPerformance" />


  </main>
</template>

<script>
import EventItem from "@/components/EventItem";
import SessionItem from "@/components/SessionItem";
import Datepicker from "vuejs-datepicker";
import ModalAdvisory from "@/components/modals/ModalAdvisory";
import { en, zh } from "vuejs-datepicker/dist/locale";
import FancyButton from "@/components/generic/FancyButton";
import moment from "moment";
import _ from "lodash";

import { mapState, mapActions } from 'vuex';

export default {
  name: "choose-date-time",
  components: {
    EventItem,
    Datepicker,
    ModalAdvisory,
    FancyButton,
    SessionItem
  },
  data() {
    return {
      itemSelected: null,
      calendarDateSelected: null,
      calendarVisible: false,
      icAttributesList: []
    };
  },
  watch: {
    "$i18n.locale": function () {
      this.initCreated();
      this.initBeforeMount();
    }
  },
  created() {
    this.setPreview(location.search)
    this.initCreated();
  },
  async beforeMount() {
    await this.initBeforeMount();
  },
  methods: {
    initCreated() {
      this.updateStage('choose-date-time')
      this.updateLoading(true);
      this.clearError();
      this.resetProgressBar();
      this.resetEventStore();
      this.resetPerformanceStore();
    },
    async initBeforeMount() {
      if (!this.onRegistrationPath) {
        await this.retrieveEventPerformances(this.$route.params.icc);
      } else {
        await this.retrieveRegistrationSessions(this.$route.params.event);

        // If we only have one item we can skip straight to the form.
        if (this.itemsToDisplay.length === 1) {
          this.itemSelected = this.itemsToDisplay[0].CRMEventID;
          this.registrationSubmit();
        }
      }

      // Assuming the response is stored in `this.response`
      if (this.response && this.response.data && this.response.data.icAttributesList) {
        this.icAttributesList = this.response.data.icAttributesList;
      }
    },
    ppSubmitCheck() {
      this.showAdvisory();
    },
    selectItem(id) {
      this.itemSelected = id;
      this.updateProductId(this.itemSelected);
    },
    toggleCalendar() {
      this.calendarVisible = !this.calendarVisible;
    },
    showAdvisory() {
      if (this.event.advisory.length > 0 && false) {
        this.$modal.show("modal-advisory");
      } else {
        this.submitPerformance();

      }
    },
    async submitPerformance() {
      this.scrollToTopOfPurchasePath();
      this.updateProductId(this.itemSelected);

      if (this.event.icAttributesList.includes("512")) {
        // General admission flow with no seatmap
        this.updateLoading(true);
        this.updateProgressBarPath({ key: "paidEventNoSeatAllocation", value: true });

        await this.retrieve512EventDetails();

        this.updateMode("GA");
        this.updateStage("choose-tickets");
      } else {
        this.updateStage("seating-area");
      }
    },
    selectDate(event) {
      // this item is already selected, so disable it
      if (Number(this.calendarDateSelected) == Number(event)) {
        this.calendarDateSelected = null
      } else {
        this.calendarDateSelected = event
      }
    },
    registrationSubmit() {
      // Safer to filter than to use index on a computed array above
      // as the index position may not as assosciate with the original array (this.event.sessionsFound)
      // number of results affected by calendar
      let sessionToSubmit = this.event.sessionsFound.filter(item => {
        return item.CRMEventID == this.itemSelected
      });

      sessionToSubmit = sessionToSubmit[0]

      this.updateSessionSelected(sessionToSubmit);
      this.updateProductId(this.itemSelected);
      this.$router.push({ name: "registration-details", params: { event: this.$route.params.event, locale: this.$i18n.locale } })
    },
    ...mapActions([
      'updateLoading',
      'clearError',
      'updateSessionSelected',
      'updateStage',
      'retrieveEventPerformances',
      'retrieveRegistrationSessions',
      'updateProductId',
      'retrieve512EventDetails',
      'updateProgressBarPath',
      'updateMode',
      'resetEventStore',
      'resetPerformanceStore',
      'resetProgressBar',
      'scrollToTopOfPurchasePath',
      'setPreview'
    ])
  },
  computed: {
    onRegistrationPath() {
      return this.$route.name.includes('registration')
    },
    getTitle() {
      return this.event.registrationEvent.title
    },
    icAttributesList() {
      return this.icAttributesList
    },
    shouldDisplayShowTitle() {
      return this.event.icAttributesList.includes("64");
    },
    shouldDisplayLocalisedDate() {
      return !this.event.icAttributesList.includes("16");
    },
    calendarLanguage() {
      // Client requests that TC / SC calendars both render in the SC format
      switch (this.$i18n.locale) {
        case "en":
          return en;
        case "tc":
        case "sc":
          return zh;
        default:
          return en;
      }
    },
    highlightedDates() {
      // This was disabledDates
      // The only change is the return is now: match, instead of !match
      let date = new Date();
      date.setDate(date.getDate() - 1);

      return {
        to: date,
        customPredictor: (date) => {
          let match = false;
          this.event.activeDates.forEach((item) => {
            if (!match) {
              match = item.isSame(date, "day");
            }
          });
          return match;
        },
      };
    },
    itemsToDisplay() {
      // An event has been selected close the calendar
      this.calendarVisible = false;

      // The calendar is fixed to the bottom on mobile, so scroll to the top on selection (as there could be a lot of events)
      if (window.innerWidth < 1024) window.scrollTo(0, 0);

      let context = "performancesFound"

      if (this.onRegistrationPath) {
        context = "sessionsFound";
      }

      if (this.calendarDateSelected) {
        let itemsToReturn = [];

        this.event[context].forEach((item) => {
          if (moment(this.calendarDateSelected).tz('Asia/Hong_Kong').isSame(item.showDateTime, "day")) {
            // display performances from the date selected
            itemsToReturn.push(item);
          } else if (item.productId == this.itemSelected) {
            // if there is a performance selected from another date, remove its selection
            this.itemSelected = null;
          }
        });

        return itemsToReturn;
      } else {
        return this.event[context];
      }
    },
    openDate() {
      if (this.event.activeDates.length > 0) {
        return this.event.activeDates[0].toDate();
      } else {
        return null;
      }
    },
    showChooseDateTime() {
      if (this.error == 'date_times_retrieval_failed' || this.itemsToDisplay.length == 0) {
        return false;
      }

      return true;
    },
    getSponsor() {
      return _.get(this.event.sponsorIccReferences, this.$route.params.icc, null)
    },
    ...mapState(['event', 'performance', 'error'])
  },
};
</script>
